import { Button, Input, Label, Sheet, SheetContent, SheetHeader, SheetTitle } from '@miyuki/ui';
import * as React from 'react';

import { t } from '@/lib/i18n';

/**
 * Situarse en una fila sin confirmar una por una (RF-51).
 *
 * Es lo que resuelve retomar una pulsera empezada antes de instalar la
 * aplicación: saltar a la fila 37 son dos toques y no treinta y siete.
 */
export interface GoToRowSheetProps {
  readonly open: boolean;
  readonly totalRows: number;
  /** Recibe el índice **base 0**, como lo espera el dominio. */
  readonly onGo: (row: number) => void;
  readonly onOpenChange: (open: boolean) => void;
}

export function GoToRowSheet(props: GoToRowSheetProps) {
  // Remontar al abrir vacía el campo, que es lo que React recomienda en vez de
  // sincronizar estado con un efecto. Mismo recurso que `SaveDesignSheet`.
  return <GoToRowForm key={props.open ? 'open' : 'closed'} {...props} />;
}

function GoToRowForm({ open, totalRows, onGo, onOpenChange }: GoToRowSheetProps) {
  const [value, setValue] = React.useState('');
  const [rejected, setRejected] = React.useState(false);

  const submit = () => {
    const parsed = Number(value.trim());

    // Una fila que no existe es un error de quien la pidió, no un valor a
    // acotar en silencio: acotarlo la dejaría en una fila que no pidió (RF-51e).
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > totalRows) {
      setRejected(true);
      return;
    }

    onGo(parsed - 1);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="pb-8">
        <SheetHeader>
          <SheetTitle>{t('trackingGoToRow')}</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-3 px-4 pb-2">
          <Label htmlFor="go-to-row">{t('trackingGoToRowLabel')}</Label>
          <Input
            id="go-to-row"
            className="h-11"
            type="number"
            inputMode="numeric"
            min={1}
            max={totalRows}
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
              setRejected(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
            }}
          />

          {rejected && (
            <p className="text-destructive text-sm">
              {t('trackingGoToRowInvalid', { total: totalRows })}
            </p>
          )}

          <Button size="touch" onClick={submit}>
            {t('trackingGoToRow')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
